import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, Switch, Toolbar, ToolbarContent, ToolbarItem, Button, } from '@patternfly/react-core';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { Trans, useTranslation } from 'react-i18next';
import { PlusIcon } from '@patternfly/react-icons'

import RoiCustomizationForm from './RoiCustomizationForm';
import '../customization-page.css';
import { columnIds, columnLangKeys, columnTooltips } from './ContourRoiTable';
import { StoreState } from '../../../store/store';
import { appConfigSelectors } from '../../../store/appConfig/appConfigSlice';
import { advancedRoiCustomizationAdded, allRoisInCustomizationOutputToggled, contouringSelectors, metadataItemAdded, metadataItemRemoved, metadataItemUpdated, modelCustomizationDescriptionUpdated, modelVisibilityUpdated, scrollToViewFromModelCustomizationMetadataRemoved, activeTagFilterToggled, activeModalityFilterToggled, activeVersionTagFilterToggled as contourActiveVersionTagFilter } from '../../../store/contouring/contouringSlice';
import { makeSelectIsAnyMetadataForOutputModified, makeSelectIsAnyRoiForOutputModified, makeSelectIsAnyTriggerForBaseModified, makeSelectRoiCustomizationsForOutput } from '../../../store/contouring/contouringSelectors';
import { CustomizationObjectType, Modality, ModalityDirection, ModelVisibility } from '../../../store/global-types/customization-types';
import { RTSTRUCT_METADATA_ATTRIBUTE_PRESETS } from '../../../store/contouring/contouring-types';
import ContourSelectionRules from '../../selection/contour/ContourSelectionRules';
import CustomizationSection from '../CustomizationSection';
import CustomizationForm from '../CustomizationForm';
import { generateNewId } from '../../../store/global-types/customization-helpers';
import { collectDicomAttributeIds, computeValidationStatus, filterTriggerFormErrors } from '../../../store/global-types/store-errors';

interface ContourCustomizationFormProps {
    customizationOutputId: string,
}

const ContourCustomizationForm = (props: ContourCustomizationFormProps) => {
    const { customizationOutputId } = props;
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);

    const customizationOutput = useSelector((state: StoreState) => contouringSelectors.selectOutputById(state, customizationOutputId));
    const customizationBase = useSelector((state: StoreState) => customizationOutput ? contouringSelectors.selectCustomizationBaseById(state, customizationOutput.modelCustomizationBaseId) : undefined);
    const model = useSelector((state: StoreState) => customizationBase ? contouringSelectors.selectModelById(state, customizationBase.modelId) : undefined);
    const validationEntities = useSelector(contouringSelectors.selectCustomizationValidationErrorEntities);
    const formValidationErrors = useSelector(contouringSelectors.selectFormValidationErrors);
    const dicomRuleEntities = useSelector(contouringSelectors.selectDicomRuleEntities);
    const outputMetadata = useSelector(contouringSelectors.selectOutputMetadataEntities);
    const originalMetadata = useSelector(contouringSelectors.selectOriginalOutputMetadataEntities);

    // Get active filters for visual feedback
    const activeTags = useSelector(contouringSelectors.selectActiveTags);
    const activeVersionTags = useSelector(contouringSelectors.selectActiveVersionTags);
    const activeInputModalities = useSelector(contouringSelectors.selectActiveInputModalities);
    const activeOutputModalities = useSelector(contouringSelectors.selectActiveOutputModalities);

    const selectRoiCustomizationsForOutput = useMemo(makeSelectRoiCustomizationsForOutput, []);
    const roiCustomizations = useSelector((state: StoreState) => selectRoiCustomizationsForOutput(state, customizationOutputId));

    const selectIsAnyRoiForOutputModified = useMemo(makeSelectIsAnyRoiForOutputModified, []);
    const isAnyRoiModified = useSelector((state: StoreState) => selectIsAnyRoiForOutputModified(state, customizationOutputId));

    const selectIsAnyMetadataForOutputModified = useMemo(makeSelectIsAnyMetadataForOutputModified, []);
    const isAnyMetadataModified = useSelector((state: StoreState) => selectIsAnyMetadataForOutputModified(state, customizationOutputId));

    const selectIsAnyTriggerRuleForBaseModified = useMemo(makeSelectIsAnyTriggerForBaseModified, []);
    const isAnyTriggerRuleModified = useSelector((state: StoreState) => customizationOutput ? selectIsAnyTriggerRuleForBaseModified(state, customizationOutput.modelCustomizationBaseId) : false);

    const [isRoisSectionExpanded, setRoisSectionExpanded] = useState(true);
    const [areAllRoisIncluded, setAreAllRoisIncluded] = useState(true);

    const handleToggleRoiCustomizationExpanded = useCallback(() => {
        setRoisSectionExpanded(!isRoisSectionExpanded);
    }, [isRoisSectionExpanded]);

    const handleSetUndoState = useCallback(() => { }, []);

    const onSetModelVisibility = useCallback((modelId: string, visibility: ModelVisibility) => {
        dispatch(modelVisibilityUpdated({ modelId, visibility }));
    }, [dispatch]);

    const onDescriptionChanged = useCallback((text: string) => {
        if (customizationBase) {
            dispatch(modelCustomizationDescriptionUpdated({ customizationBaseId: customizationBase.id, description: text }));
        }
    }, [dispatch, customizationBase]);

    const handleSelectAllRois = useCallback((_: unknown, includeAllRois: boolean) => {
        if (customizationOutput) {
            dispatch(allRoisInCustomizationOutputToggled({ customizationOutputId: customizationOutput.id, isIncluded: includeAllRois }));
            setAreAllRoisIncluded(includeAllRois);
        }
    }, [dispatch, customizationOutput]);

    const handleAddMetadataEntry = useCallback((customizationOutputId: string) => {
        dispatch(metadataItemAdded(customizationOutputId));
    }, [dispatch]);

    const handleAddAdvancedStructure = useCallback(() => {
        const newRoiId = generateNewId();
        dispatch(advancedRoiCustomizationAdded({ outputId: customizationOutputId, newRoiId: newRoiId }));
    }, [dispatch, customizationOutputId]);


    useEffect(() => {
        // update the state of the "all rois included" switch
        if (customizationOutput && customizationOutput.rois.length > 0) {
            setAreAllRoisIncluded(roiCustomizations.every(r => r.isIncluded));
        }
    }, [customizationOutput, customizationBase, roiCustomizations]);

    const onMetadataItemUpdated = useCallback((metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean) => {
        dispatch(metadataItemUpdated({ metadataId, attribute, value, isUndoOperation }));
    }, [dispatch]);

    const onMetadaItemRemoved = useCallback((metadataId: string) => {
        dispatch(metadataItemRemoved(metadataId));
    }, [dispatch]);

    const removeScrollToViewFromMetadataItem = useCallback((metadataId: string) => {
        dispatch(scrollToViewFromModelCustomizationMetadataRemoved(metadataId));
    }, [dispatch]);

    const handleToggleModelTag = useCallback((tag: string) => {
        dispatch(activeTagFilterToggled(tag));
    }, [dispatch]);

    const handleToggleModelModality = useCallback((modality: Modality, direction: ModalityDirection) => {
        dispatch(activeModalityFilterToggled({ modality, direction }));
    }, [dispatch]);

    const handleToggleModelVersionTag = useCallback((tag: string) => {
        dispatch(contourActiveVersionTagFilter(tag));
    }, [dispatch]);

    const includedCount = useMemo(() => roiCustomizations.filter(r => r.isIncluded).length, [roiCustomizations]);
    const totalStructureCount = roiCustomizations.length;

    // sanity check that we have retrieved valid objects from redux store
    // (this check has to be done after all hooks calls)
    if (customizationOutput === undefined || customizationBase === undefined || model === undefined) {
        return null;
    }


    const roisHaveBackendErrors = customizationOutput.rois.some(rId => validationEntities[rId]?.type === CustomizationObjectType.Roi);
    const roiFormErrors = formValidationErrors.filter(e => customizationOutput.rois.includes(e.itemId) && e.itemType === CustomizationObjectType.Roi);
    const roisValidationStatus = computeValidationStatus(roisHaveBackendErrors, roiFormErrors);

    const dicomAttributeIds = collectDicomAttributeIds(customizationBase.dicomRules, dicomRuleEntities);
    const triggerFormErrors = filterTriggerFormErrors(formValidationErrors, customizationBase.aeTitleRules, customizationBase.dicomRules, dicomAttributeIds);

    return (
        <CustomizationForm
            model={model}
            customizationBase={customizationBase}
            customizationOutput={customizationOutput}
            validationEntities={validationEntities}
            triggerFormValidationErrors={triggerFormErrors}
            outputMetadata={outputMetadata}
            originalMetadata={originalMetadata}
            metadataPresets={RTSTRUCT_METADATA_ATTRIBUTE_PRESETS}
            isAnyMetadataModified={isAnyMetadataModified}
            isAnyTriggerRuleModified={isAnyTriggerRuleModified}
            onModelTagToggled={handleToggleModelTag}
            onModelVersionTagToggled={handleToggleModelVersionTag}
            onModelModalityToggled={handleToggleModelModality}
            onAddMetadataEntry={handleAddMetadataEntry}
            onSetModelVisibility={onSetModelVisibility}
            onDescriptionChanged={onDescriptionChanged}
            onMetadataItemUpdated={onMetadataItemUpdated}
            onMetadaItemRemoved={onMetadaItemRemoved}
            removeScrollToViewFromMetadataItem={removeScrollToViewFromMetadataItem}
            activeTags={activeTags}
            activeVersionTags={activeVersionTags}
            activeInputModalities={activeInputModalities}
            activeOutputModalities={activeOutputModalities}
            contentSummary={
                (<Text component="p" className="selectable">
                    <Trans i18nKey="customizationPage.includedCounts">
                        Currently structure sets generated with this customization will include <b>{{ includedCount }}</b> out of <b>{{ totalStructureCount }}</b> available structures.
                    </Trans>
                </Text>
                )}
            selectionRules={(
                <ContourSelectionRules
                    customizationBaseId={customizationBase.id}
                    isUndoDisabled={true}
                    setUndoState={handleSetUndoState}
                    heading={`model-selection-for-${customizationBase.id}`}
                    hideCustomizationName
                    showMultipleOutputsWarning={customizationBase.outputs.length > 1}
                />
            )}
        >

            <CustomizationSection
                label={t('customizationPage.roisSection.title')}
                isModified={isAnyRoiModified}
                validationStatus={roisValidationStatus}
                itemCount={includedCount}
                totalCount={customizationOutput.rois.length}
                collapsedMessage={t('customizationPage.roisSection.message')}
                isExpanded={isRoisSectionExpanded}
                onClick={handleToggleRoiCustomizationExpanded}
            >
                <Table variant='compact' isStickyHeader className="mv-sticky-fix" data-testid="contour-rois-table">
                    <Thead aria-label='Contour ROIs'>
                        <Tr className='customization-form-table'>
                            <Th aria-label='Is modified?'></Th>
                            <Th aria-label='Structure Name'>{t(columnLangKeys[columnIds.name])}</Th>
                            <Th aria-label='Is structure included in structure set?' info={{ tooltip: columnTooltips[columnIds.isIncluded](t) }} className='enabled-col-th'>{t(columnLangKeys[columnIds.isIncluded])}</Th>
                            <Th aria-label='Color'>{t(columnLangKeys[columnIds.color])}</Th>
                            <Th aria-label='Operation' info={{ tooltip: columnTooltips[columnIds.operation](t, deploymentInfo?.operationsManualUrl) }}>{t(columnLangKeys[columnIds.operation])}</Th>
                            <Th aria-label='Interpreted Type'>{t(columnLangKeys[columnIds.interpretedType])}</Th>
                            <Th aria-label='Coding scheme value' info={{ tooltip: columnTooltips[columnIds.fmaCodeValue](t, deploymentInfo?.defaultRoiCodesUrl) }}>{t(columnLangKeys[columnIds.fmaCodeValue])}</Th>
                            <Th aria-label='Coding scheme code meaning'>{t(columnLangKeys[columnIds.fmaCodeMeaning])}</Th>
                            <Th aria-label='Coding scheme designator'>{t(columnLangKeys[columnIds.fmaCodeSchemeDesignator])}</Th>
                            <Th aria-label='Action menu'></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        <Tr>
                            <Td></Td>
                            <Td></Td>
                            <Td aria-label="Toggle inclusion of all structures">
                                <Switch
                                    aria-label="Toggle inclusion of all structures"
                                    id={'select-all-rois-switch'}
                                    isChecked={areAllRoisIncluded}
                                    onChange={handleSelectAllRois}
                                />
                            </Td>
                            <Td></Td>
                            <Td></Td>
                            <Td></Td>
                            <Td></Td>
                            <Td></Td>
                            <Td></Td>
                            <Td></Td>
                        </Tr>

                        {customizationOutput.rois.map(rId => <RoiCustomizationForm roiId={rId} key={rId} roiIdList={customizationOutput.rois} />)}
                    </Tbody>
                </Table>
                <div className="contour-rois-toolbar-section">
                    <Toolbar>
                        <ToolbarContent>
                            <ToolbarItem>
                                <Button
                                    data-testid="add-advanced-roi-button-form"
                                    variant="secondary"
                                    icon={<PlusIcon />}
                                    onClick={handleAddAdvancedStructure}>{t('customizationToolbar.addAdvancedRoi')}</Button>
                            </ToolbarItem>
                        </ToolbarContent>
                    </Toolbar>
                </div>
            </CustomizationSection>
        </CustomizationForm>
    );
}

export default ContourCustomizationForm;
